"use client"

import React, { useEffect, useMemo, useState } from 'react'
import HubspotMeetingsSection from '@/components/HubspotMeetingsSection'
import Header from '@/components/Header'
import SecondaryServicesNav from '@/components/SecondaryServicesNav'
import Popup from '@/components/Popup'
import HTMLRenderer from '@/components/ui/html-renderer'
import { FaWhatsapp, FaFacebookF, FaLinkedinIn, FaInstagram, FaTiktok } from 'react-icons/fa'
import { FiMail, FiGlobe } from 'react-icons/fi'
import { GoLocation } from 'react-icons/go'
import { GrDocumentText } from 'react-icons/gr'
import { IoMdBusiness } from 'react-icons/io'
import { MdTravelExplore } from 'react-icons/md'
// Load officeMaps dynamically to avoid bundling the interactive map on pages
import type { Product } from '@/lib/productsStore'
import { useI18n } from '@/context/i18n'

const InfoCard = ({ href, icon, title, subtitle }: { href?: string; icon: React.ReactNode; title: string; subtitle: string }) => (
  <a
    href={href || '#'}
    target="_blank"
    rel="noopener noreferrer"
    className="flex items-center w-full gap-5 bg-white border border-neutral-200 p-4 hover:bg-neutral-50 transition-colors"
  >
    <div className="text-[#fb0009] w-[50px] h-[50px] flex-shrink-0 flex items-center justify-center">{icon}</div>
    <div className="text-left">
      <p className="text-neutral-900 font-montserrat font-semibold text-base md:text-lg">{title}</p>
      <span className="text-neutral-600 font-barlow text-sm font-normal">{subtitle}</span>
    </div>
  </a>
)

const SocialLink = ({ href, icon }: { href: string; icon: React.ReactNode }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className="text-[#fb0009] transition-transform hover:scale-110 hover:text-red-700">{icon}</a>
)

type AdminSocial = { id: string; url: string; url_en?: string }
type Props = {
  product: Partial<Product> & {
    advisorName?: string
    avatar?: string
    socialLinks?: (AdminSocial | { id: string; url: string; icon: React.ReactNode })[]
    representations?: { location: string; contact?: string }[]
    primaryOfficeId?: string | number
    freeConsultUrl?: string
    hubspotMeetingsUrl?: string
  }
}

export default function AdvisorTemplateClient({ product }: Props) {
  const { lang } = useI18n()
  const [podcast, setPodcast] = useState<any | null>(null)
  const [trends, setTrends] = useState<any[]>([])
  const [isPopupOpen, setPopupOpen] = useState(false)

  useEffect(() => {
    fetch('/api/podcast/last').then(r => r.json()).then(j => setPodcast(j?.item || null)).catch(() => setPodcast(null))
  }, [])

  // dynamic import of officeMaps (small data file) to avoid pulling in map libs
  const [officeMaps, setOfficeMaps] = useState<any[]>([])
  useEffect(() => {
    let mounted = true
    import('@/components/interactive-map').then((mod) => {
      if (!mounted) return
      const maps = (mod && (mod.officeMaps || mod.offices || [])) as any[]
      setOfficeMaps(Array.isArray(maps) ? maps : [])
    }).catch(() => {
      if (!mounted) return
      setOfficeMaps([])
    })
    return () => { mounted = false }
  }, [])

  useEffect(() => {
    fetch('/api/trending')
      .then(r => r.json())
      .then(data => setTrends(Array.isArray(data?.items) ? data.items : []))
      .catch(() => setTrends([]))
  }, [])
  const [serviceItems, setServiceItems] = useState<any[]>([])
  useEffect(() => {
    fetch('/api/services?category=travel')
      .then(r => r.json())
      .then(j => {
        const arr = Array.isArray(j?.items) ? j.items : []
        setServiceItems(arr.slice(0, 5))
      })
      .catch(() => setServiceItems([]))
  }, [])

  // officeMaps now provided by dynamic import above
  const representations = product.representations || []
  const primaryOfficeId = product.primaryOfficeId ? Number(product.primaryOfficeId) : undefined
  const primaryOffice = officeMaps.find(o => o.id === primaryOfficeId)
  const defaultRepresentations = [
    { location: 'Nuevo León, Monterrey', contact: 'https://wa.me/5218183661646' },
    { location: 'Guadalajara', contact: 'https://wa.me/5213328220050' },
    { location: 'Vancouver', contact: 'https://wa.me/14163030259' },
    { location: 'CDMX, Ciudad de México', contact: 'https://wa.me/527122315859' },
  ]
  const displayRepresentations = (representations && representations.length > 0) ? representations : defaultRepresentations
  const defaultSocials = [
    { id: 'wa', url: 'https://wa.me/message/5217122315859' },
    { id: 'ml', url: 'mailto:hola@grupo-let.com' },
    { id: 'fb', url: 'https://www.facebook.com/LinkEducationTravel' },
    { id: 'ln', url: 'https://www.linkedin.com/company/linkeducationandtravel' },
    { id: 'ig', url: 'https://www.instagram.com/linkeducationandtravel/' },
  ]

  const rawSocials: (AdminSocial | { id: string; url: string; url_en?: string; icon: React.ReactNode })[] = (product.socialLinks && product.socialLinks.length > 0) ? product.socialLinks : defaultSocials

  const socialLinks = rawSocials.map(s => {
    if ((s as any).icon) return s as any
    const id = s.id
    // choose language-specific url when available; fallback to spanish url
    const url = (lang === 'EN') ? ((s as any).url_en || (s as any).url) : (s as any).url
    let icon: React.ReactNode = <FiGlobe size={20} />
    if (id === 'wa') icon = <FaWhatsapp size={20} />
    else if (id === 'ml') icon = <FiMail size={20} />
    else if (id === 'fb') icon = <FaFacebookF size={20} />
    else if (id === 'ln') icon = <FaLinkedinIn size={20} />
    else if (id === 'ig') icon = <FaInstagram size={20} />
    else if (id === 'tk') icon = <FaTiktok size={28} />
    return { id, url, icon }
  })

  const meetingUrl = (product.hubspotMeetingsUrl || '').trim()
  const meetingEmbedUrl = meetingUrl
    ? (meetingUrl.includes('embed=true')
        ? meetingUrl
        : `${meetingUrl}${meetingUrl.includes('?') ? '&' : '?'}embed=true`)
    : ''



  // Show only the main office locations as 'Ubicación' on the card (Toluca, Toronto, Metepec)
  const filteredOffices = useMemo(() => (officeMaps || []).filter((o) => [1, 2, 3].includes(Number(o.id))), [officeMaps])
  const heroLocationLabel = useMemo(() => {
    const base = (lang === 'EN'
      ? (primaryOffice?.location_en || primaryOffice?.location || filteredOffices[0]?.location || '-')
      : (primaryOffice?.location || filteredOffices[0]?.location || '-'))
    if ((product.slug || '').toLowerCase() === 'manuel') {
      return 'México'
    }
    return base
  }, [filteredOffices, lang, primaryOffice, product.slug])

  return (
    <>
    <Header />
      <SecondaryServicesNav />
    <div className="bg-white text-neutral-900 pt-20">
      
      <main className="flex flex-col items-center overflow-x-hidden">
        <div className="relative w-full max-w-lg px-4 pt-0">
          {/* Adornos colocados fuera del padding para quedar alineados con las esquinas */}
          <div className="absolute -top-4 right-4 h-24 w-40 z-10 pointer-events-none" aria-hidden>
            <svg viewBox="0 0 296.4 97.81" className="w-full h-full" preserveAspectRatio="none">
                <polygon fill="#e3e3e3" points="296.4 0, 0 0, 296.4 97.81" />
                <polygon fill="#fb0009" points="296.4 0, 70.78 0, 296.4 97.81" />
                <polygon fill="#000000ff" points="296.4 0, 33.86 0, 296.4 60.24" />
            </svg>
          </div>
          <section className="relative w-full bg-white border border-neutral-200 rounded-lg flex flex-col items-center text-center py-8 px-4 overflow-hidden">
            {/* Adorno Inferior Izq. ligado a la primera card */}
            <div className="absolute -bottom-4 -left-4 h-24 w-40 z-0 pointer-events-none" aria-hidden>
              <svg viewBox="0 0 296.4 97.81" className="w-full h-full" preserveAspectRatio="none">
                  <polygon fill="#e3e3e3" points="0 97.81, 296.4 97.81, 0 0" />
                  <polygon fill="#fb0009" points="0 97.81, 225.62 97.81, 0 0" />
                  <polygon fill="#000000ff" points="0 97.81, 262.54 97.81, 0 37.57" />
              </svg>
            </div>
          <div className="relative z-10 w-full flex flex-col items-center">
              {/* ornaments moved outside inner content to avoid padding offsets */}

              <div className="w-1/2 mb-4">
                <img src={product.avatar || product.heroImages?.[0] || 'https://grupo-let.com/wp-content/uploads/2025/09/Ellipse-21.png'} alt={product.advisorName || product.title || 'Asesor'} className="w-full h-auto " loading="lazy" width={300} height={300} />
              </div>
              <h4 className="text-3xl font-bold font-montserrat text-neutral-900">
                {product.advisorName || (lang === 'EN' ? (product.title_en || product.title) : product.title)}
                <br />
                <hr style={{ border: '1px solid #e3e3e3'}} />
                <span className="text-2xl text-dark">{lang === 'EN' ? (product.subtitle_en || product.subtitle || '') : (product.subtitle || '')}</span>
              </h4>
              
              <div className="flex justify-center w-full mt-6">
                <a href={(primaryOffice?.mapsUrl || filteredOffices[0]?.mapsUrl) || '#'} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center justify-center p-2 transition-transform hover:scale-105 pr-14">
                  <GoLocation size={35} className="text-[#fb0009]" />
                  <p className="mt-2 text-sm font-semibold font-montserrat text-neutral-900">{heroLocationLabel}</p>
                </a>
                <button onClick={() => setPopupOpen(true)} className="flex flex-col items-center justify-center p-2 transition-transform hover:scale-105">
                  <GrDocumentText size={30} className="text-[#fb0009]" />
                  <p className="mt-2 text-sm font-semibold font-montserrat text-neutral-900">{lang === 'EN' ? 'About' : 'Sobre mí'}</p>
                </button>
              </div>
              <div className="w-full px-4 mt-4 sm:px-10 md:px-16">
                <a href={product.freeConsultUrl || socialLinks[0]?.url || '#'} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-full gap-3 bg-[#fb0009] rounded-md py-2 px-4 shadow-lg shadow-red-300/40 transition-transform hover:scale-105">
                  <FaWhatsapp size={28} className="text-white" />
                  <span className="text-base font-semibold font-montserrat text-white">{lang === 'EN' ? 'Free Consultation' : 'Asesoría Gratuita'}</span>
                </a>
              </div>
              <br />
              <h6 className="mt-4 text-xl font-bold md:text-2xl font-montserrat text-neutral-900">{lang === 'EN' ? 'My networks' : 'Mis redes'}</h6>
              <div className="flex flex-wrap items-center justify-center gap-4 mt-4 pb-14">
                {socialLinks.map(link => <SocialLink key={link.id} href={link.url} icon={link.icon} />)}
              </div>              
            </div>
          </section>
          
          <nav className="flex flex-wrap justify-center w-full gap-6 p-5 my-5 text-neutral-800 md:gap-8 font-montserrat border-b border-neutral-200" suppressHydrationWarning>
            <a href="#podcast" className="underline hover:text-red-500">Podcast</a>
            <a href="#tendencias" className="underline hover:text-red-500">{lang === 'EN' ? 'Trends' : 'Tendencias'}</a>
            <a href="#oficinas" className="underline hover:text-red-500">{lang === 'EN' ? 'Offices' : 'Oficinas'}</a>
            <a href="#agenda" className="underline hover:text-red-500">{lang === 'EN' ? 'Schedule' : 'Agenda'}</a>
          </nav>

          <HubspotMeetingsSection
            meetingEmbedUrl={meetingEmbedUrl}
            meetingUrl={meetingUrl}
            enabled={!!meetingEmbedUrl}
            lang={lang}
          />

          
          <section id="podcast" className="flex flex-col items-center w-full mt-12">
            <h2 className="mb-3 text-2xl md:text-3xl font-extrabold tracking-tight text-neutral-900 text-center">{lang === 'EN' ? 'Latest' : 'Último'} <span className="red-highlight">Podcast</span></h2>
            <div className="h-1 w-12 bg-[#fb0009] rounded mb-4" />
            <div className="w-full p-1 bg-white border border-neutral-200 rounded-lg">
              {podcast?.id ? (
                <div>
                  <div className="aspect-video w-full overflow-hidden rounded">
                    <iframe className="w-full h-full" src={`https://www.youtube.com/embed/${podcast.id}?rel=0`} title={podcast.title} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                  </div>
                  <p className="mt-3 p-3 font-semibold text-neutral-900">{lang === 'EN' ? (podcast.title_en || podcast.title) : podcast.title}</p>
                </div>
              ) : (
                <div className="w-full p-8 text-center text-neutral-600 border border-dashed rounded-lg border-neutral-300">
                  <p>Cargando el último episodio...</p>
                </div>
              )}
            </div>
          </section>

          <section id="tendencias" className="flex flex-col items-center w-full mt-12">
            <h2 className="mb-3 text-2xl md:text-3xl font-extrabold tracking-tight text-neutral-900 text-center">{lang === 'EN' ? 'Trends' : 'Tendencias'}</h2>
            <div className="h-1 w-12 bg-[#fb0009] rounded mb-4" />
            <div className="flex flex-col w-full gap-4">
              {trends.map((t: any) => (
                <a key={t.id} href={t.href || '#'} className="block bg-white border border-neutral-200 rounded-lg overflow-hidden hover:bg-neutral-50 transition-colors">
                  {t.image && <img src={t.image} alt={t.title} className="w-full object-cover aspect-video" loading="lazy" />}
                  <div className="p-4">
                    {t.category && <span className="inline-block text-xs font-bold bg-[#fb0009] text-white px-2 py-0.5 mb-2 rounded">{t.category}</span>}
                    <p className="font-semibold text-neutral-900">{lang === 'EN' ? (t.title_en || t.title) : t.title}</p>
                  </div>
                </a>
              ))}
            </div>
          </section>

          <section id="oficinas" className="flex flex-col items-center w-full pb-12 mt-12">
            <h2 className="mb-3 text-2xl md:text-3xl font-extrabold tracking-tight text-neutral-900 text-center">{lang === 'EN' ? 'Our' : 'Nuestras'} <span className="red-highlight">{lang === 'EN' ? 'Offices' : 'Oficinas'}</span></h2>
            <div className="h-1 w-12 bg-[#fb0009] rounded mb-4" />
            <div className="flex flex-col w-full gap-4">
              {/* Explicit main offices in the desired order: Toluca (1), Toronto (2), Metepec (3) */}
              {([1,2,3] as number[]).map(id => {
                const office = (officeMaps || []).find((o: any) => Number(o.id) === id)
                if (!office) return null
                return (
                  <InfoCard key={`office-${office.id}`} href={office.mapsUrl} icon={<GoLocation size={30} />} title={lang === 'EN' ? 'Location' : 'Ubicación'} subtitle={office.location} />
                )
              })}

              {/* Representations (defaults or product-provided) */}
              {displayRepresentations.map((rep: any) => (
                <InfoCard key={`rep-${rep.location}`} href={rep.contact} icon={<IoMdBusiness size={30} />} title={lang === 'EN' ? 'Representation' : 'Representación'} subtitle={rep.location} />
              ))}
            </div>
          </section>


        </div>
      </main>

      <Popup isOpen={isPopupOpen} onClose={() => setPopupOpen(false)} contentClassName="max-w-md">
        {/* Header: foto + nombre */}
        <div className="flex flex-col items-center text-center mb-5">
          {product.avatar && (
            <img
              src={product.avatar}
              alt={product.advisorName || product.title || 'Asesor'}
              className="w-20 h-20 rounded-full object-cover border-4 border-[#fb0009] shadow-lg mb-3"
              loading="lazy"
            />
          )}
          <h2 className="text-xl font-extrabold font-montserrat text-neutral-900 leading-tight">
            {lang === 'EN' ? 'About' : 'Sobre'} {product.advisorName || (lang === 'EN' ? (product.title_en || product.title) : product.title)}
          </h2>
          {product.subtitle && (
            <p className="text-sm text-neutral-500 font-barlow mt-1">{lang === 'EN' ? (product.subtitle_en || product.subtitle) : product.subtitle}</p>
          )}
        </div>

        {/* Divisor */}
        <div className="flex items-center gap-3 mb-5">
          <div className="flex-1 h-px bg-neutral-200" />
          <div className="w-8 h-1 bg-[#fb0009] rounded-full" />
          <div className="flex-1 h-px bg-neutral-200" />
        </div>

        {/* Contenido bio */}
        <div
          className="max-h-[55vh] overflow-y-auto pr-1"
          style={{ scrollbarWidth: 'thin', scrollbarColor: '#fb0009 #f0f0f0' }}
        >
          <div className="space-y-4 font-barlow text-neutral-700 text-[15px] leading-relaxed">
            <HTMLRenderer content={lang === 'EN' ? (product.description_en || product.description || '') : (product.description || '')} />
          </div>
        </div>

        {/* Botón cerrar */}
        <button
          onClick={() => setPopupOpen(false)}
          className="mt-6 w-full bg-[#fb0009] text-white font-bold font-montserrat py-3 px-5 rounded-md hover:bg-red-700 active:scale-95 transition-all text-sm tracking-wide"
        >
          {lang === 'EN' ? 'Close' : 'Cerrar'}
        </button>
      </Popup>
    </div>
    </>
  )
}
// cambio 
